import { PlusOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Divider, notification, Row, Spin } from 'antd';
import { type ReactElement, type ReactNode, useEffect, useEffectEvent, useState } from 'react';
import type { DefaultSelectSingleProps } from '@/components/types';
import type { Produto } from '@/features/produto/types/Produto';
import type { ProdutoSelecionado } from '@/features/produto/types/ProdutoSelecionado';
import type { ScrollEvent } from '@/types';
import type { FlattenOptionData } from '@/types/antd';
import { type BaseSelectOption, BaseSelectSingle } from '@/components/BaseSelectSingle';
import {
    API_PRODUTO_GET_KEY,
    apiProdutoGet,
    type ApiProdutoGetParams,
} from '@/features/produto/api/apiProdutoGet';
import { useMutationProdutoPost } from '@/features/produto/api/apiProdutoPost';
import { ProdutoOption } from '@/features/produto/components/ProdutoOption';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { useDebouncedValue } from '@/std/hooks/useDebouncedValue';
import { attempt } from '@/std/result';
import { ProdutoSelectModal } from './ProdutoSelectModal';
import {
    getProdutoSelectLocalAutomatico,
    getProdutoSelectLoteAutomatico,
    produtoPrecisaSelecao,
} from './produtoSelectUtil';

type ProdutoSelectProps = Omit<
    DefaultSelectSingleProps<number, Produto, ApiProdutoGetParams>,
    'onSelect'
> &
    Partial<{
        allLabel: string;
        canAdd: boolean;
        showInativos: boolean;
        showModal: boolean;
        onSelect: (args: ProdutoSelecionado) => void;
        onBeforeSelect: (args: ProdutoSelecionado) => boolean;
    }>;

export function ProdutoSelect({
    allLabel,
    allowClear = true,
    autoFocus = false,
    disabled,
    className,
    id,
    initialLabel,
    params,
    placeholder,
    ref,
    value,
    onChange,
    onClear,
    onDeselect,
    onSearch,
    onSelect,
    onBeforeSelect,
    canAdd = false,
    showInativos = false,
    showModal = false,
}: ProdutoSelectProps) {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [pendingSearch, setPendingSearch] = useState<string | null>(null);
    const [manualSelection, setManualSelection] = useState(false);
    const [produtoModal, setProdutoModal] = useState<Produto | null>(null);
    const [internalValue, setInternalValue] = useState<number | null>(null);

    const [searchDebounced] = useDebouncedValue(search, DEBOUNCE_TIME);

    const selectedValue = value === undefined ? internalValue : value;

    const baseParams: ApiProdutoGetParams = {
        ...params,
        orderby: 'pro_favorito desc, pro_descricao',
        pesquisar: searchDebounced,
        qtde_registros: 10,
        status: showInativos ? undefined : 1,
    };

    const getProdutos = (pageParam: number) => {
        const p: ApiProdutoGetParams = {
            ...baseParams,
            registro_inicial: pageParam * 10,
            total_registros: pageParam === 0 ? 'S' : 'N',
        };

        return apiProdutoGet({ params: p });
    };

    const enabled = typeof searchDebounced === 'string' && searchDebounced.length > 0;

    // biome-ignore assist/source/useSortedKeys: breaks type inference
    const { data, isFetching, isError, fetchNextPage } = useInfiniteQuery({
        queryKey: [API_PRODUTO_GET_KEY, baseParams, getProdutos],
        queryFn: ({ pageParam }) => getProdutos(pageParam),
        enabled,
        getNextPageParam: (_, __, lastPageParam) => lastPageParam + 1,
        initialPageParam: 0,
    });

    const total = data?.pages[0]?.total_registros ?? 0;

    const options: BaseSelectOption<number, Produto>[] =
        data && enabled
            ? data.pages.flatMap((page) =>
                  page.registros.map((produto) => ({
                      label:
                          produto.pro_idpk === selectedValue && initialLabel
                              ? initialLabel
                              : produto.pro_descricao,
                      obj: produto,
                      value: produto.pro_idpk,
                  })),
              )
            : typeof selectedValue === 'number' && initialLabel
              ? [{ label: initialLabel, obj: null, value: selectedValue }]
              : [];

    const mutationPost = useMutationProdutoPost();

    const adicionarProduto = async (): Promise<void> => {
        const result = await attempt(
            mutationPost.mutateAsync({
                body: {
                    pro_descricao: search,
                    pro_permite_mudar_preco: 1,
                },
            }),
        );

        if (result.error) {
            notification.error({
                description: result.error.message,
                title: 'Erro ao adicionar produto',
            });

            return;
        }

        notification.success({
            description: `Produto "${search}" adicionado com sucesso`,
            title: 'Sucesso',
        });
    };

    const handleSearch = (value: string): void => {
        setPendingSearch(null);
        setManualSelection(false);
        setSearch(value);
        onSearch?.(value);
    };

    const handleClear = (): void => {
        setPendingSearch(null);
        setManualSelection(false);
        setSearch('');
        onClear?.();
    };

    const commitValue = (nextValue: number | null, produto: Produto | null): void => {
        setInternalValue(nextValue);
        onChange?.(nextValue, produto);
    };

    const handleChange = (nextValue: number | null, produto: Produto | null): void => {
        if ((showModal || onBeforeSelect) && produto) {
            return;
        }

        if (allLabel && nextValue === 0) {
            setSearch('');
            setOpen(false);
        }

        commitValue(allLabel && nextValue === 0 ? null : nextValue, produto);
    };

    const handleSelect = (produto: Produto): void => {
        setPendingSearch(null);
        setManualSelection(false);
        setSearch('');
        setOpen(false);

        if (showModal) {
            if (produtoPrecisaSelecao(produto)) {
                setProdutoModal(produto);
                return;
            }
        }

        const lote = showModal ? getProdutoSelectLoteAutomatico(produto) : null;

        const selection: ProdutoSelecionado = {
            localEstoque: showModal ? getProdutoSelectLocalAutomatico(produto, null, lote) : null,
            lote,
            pacoteBarra: null,
            produto,
        };

        if (onBeforeSelect?.(selection) === false) {
            return;
        }

        if (showModal || onBeforeSelect) {
            commitValue(produto.pro_idpk, produto);
        }

        onSelect?.(selection);
    };

    const selectPendingProduto = useEffectEvent((produto: Produto) => {
        handleChange(produto.pro_idpk, produto);
        handleSelect(produto);
    });

    useEffect(() => {
        if (
            !pendingSearch ||
            pendingSearch !== search ||
            search !== searchDebounced ||
            isFetching
        ) {
            return;
        }

        setPendingSearch(null);

        if (isError || !data) {
            return;
        }

        const produtos = data.pages.flatMap((page) => page.registros);

        const exactMatches = produtos.filter(
            (produto) =>
                produto.pro_cod_barra === pendingSearch ||
                produto.produto_pacote_barra?.some(
                    (pacote) => pacote.pcb_cod_barra === pendingSearch,
                ),
        );

        const produto =
            exactMatches.length === 1
                ? exactMatches[0]
                : produtos.length === 1 && data.pages[0]?.total_registros === 1
                  ? produtos[0]
                  : undefined;

        if (produto) {
            selectPendingProduto(produto);
        }
    }, [pendingSearch, search, searchDebounced, isFetching, isError, data]);

    const handleKeyDownCapture: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (event.nativeEvent.isComposing || !(event.target instanceof HTMLInputElement)) {
            return;
        }

        if (event.key === 'Escape' || event.key === 'Tab') {
            if (event.key === 'Tab' && pendingSearch) {
                event.stopPropagation();
                setOpen(false);
            }

            setPendingSearch(null);
            setManualSelection(false);
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            setPendingSearch(null);

            if (open && search === searchDebounced && !isFetching) {
                setManualSelection(true);
            }
        }

        if (
            event.key === 'Enter' &&
            search &&
            (search !== searchDebounced ||
                isFetching ||
                options.length === 0 ||
                (!manualSelection &&
                    options.some(
                        ({ obj }) =>
                            obj?.pro_cod_barra === search ||
                            obj?.produto_pacote_barra?.some(
                                (pacote) => pacote.pcb_cod_barra === search,
                            ),
                    )))
        ) {
            // Aguarda a busca ou resolve o código exato; nos demais casos, confirma a opção ativa.
            event.preventDefault();
            event.stopPropagation();
            setPendingSearch(search);
            setOpen(true);
        }
    };

    const onPopupScroll: React.UIEventHandler<HTMLDivElement> = async (e): Promise<void> => {
        const { target } = e as unknown as ScrollEvent;

        if (
            !isFetching &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 1 &&
            options.length < total
        ) {
            await fetchNextPage();
        }
    };

    const optionRender = (
        oriOption: FlattenOptionData<BaseSelectOption<number, Produto>>,
    ): ReactNode => {
        const produto = oriOption.data.obj;

        if (produto) {
            return <ProdutoOption produto={produto} />;
        }

        return oriOption.label;
    };

    const popupRender = (menu: ReactElement): ReactElement => (
        <div className='w-full overflow-visible bg-white'>
            <Spin spinning={isFetching}>{menu}</Spin>

            {canAdd && !isFetching && search.length > 2 && (
                <>
                    <Divider style={{ margin: '4px 0' }} />

                    <Row justify='center'>
                        <Button
                            disabled={!search || search.length < 3}
                            icon={<PlusOutlined />}
                            loading={mutationPost.isPending}
                            onClick={adicionarProduto}
                            style={{ color: 'white' }}
                            tabIndex={0}
                            type='primary'
                        >
                            Adicionar novo produto
                        </Button>
                    </Row>
                </>
            )}
        </div>
    );

    return (
        <>
            <div className='contents' onKeyDownCapture={handleKeyDownCapture}>
                <BaseSelectSingle
                    allowClear={allowClear}
                    autoFocus={autoFocus}
                    className={className}
                    disabled={disabled}
                    id={id}
                    loading={isFetching}
                    onBlur={() => {
                        setPendingSearch(null);
                        setManualSelection(false);
                    }}
                    onChange={handleChange}
                    onClear={handleClear}
                    onDeselect={onDeselect}
                    onOpenChange={(nextOpen) => {
                        setOpen(nextOpen);
                        if (!nextOpen) {
                            setPendingSearch(null);
                            setManualSelection(false);
                        }
                    }}
                    onPopupScroll={onPopupScroll}
                    onSelect={handleSelect}
                    open={open}
                    optionRender={optionRender}
                    options={
                        allLabel ? [{ label: allLabel, obj: null, value: 0 }, ...options] : options
                    }
                    placeholder={placeholder}
                    popupRender={popupRender}
                    ref={ref}
                    showSearch={{
                        filterOption: false,
                        onSearch: handleSearch,
                        searchValue: search,
                    }}
                    value={allLabel ? (selectedValue ?? 0) : selectedValue}
                />
            </div>

            {showModal && produtoModal && (
                <ProdutoSelectModal
                    onClose={() => setProdutoModal(null)}
                    onSelect={(selection) => {
                        setProdutoModal(null);

                        if (onBeforeSelect?.(selection) === false) {
                            return;
                        }

                        commitValue(selection.produto.pro_idpk, selection.produto);
                        onSelect?.(selection);
                    }}
                    produto={produtoModal}
                    tabelaPrecoIdpk={params?.tabela_preco_idpk}
                />
            )}
        </>
    );
}
